import {
  Button,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { FC, useCallback, useContext, useEffect, useMemo } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSelectPricingMatrix } from '../../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import {
  getLaborName,
  ServiceItem,
  useTransformServiceItems
} from '../../../hooks/useTransformServiceItems/useTransformServiceItems'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { SubmitDialogContext } from '../../../providers/SubmitDialogProvider/SubmitDialogProvider'
import { useUpdateServiceMutation } from '../../../redux/api'
import {
  LaborApplication,
  RepairOrderExpanded,
  ServiceEntry,
  ServiceExpanded,
  ServiceInfoStatusEnum
} from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { ExpandPanel } from '../../SharedUI/ExpandPanel'
import { NoResultListItem } from '../../SharedUI/LoadingListItem'
import { LoadingMultipleArrayDataQueriesWrapper } from '../../SharedUI/LoadingMultipleArrayDataQueriesWrapper'
import { AddServiceDialogContentWrapper } from './AddServiceDialogContentWrapper'
import { SearchServiceProps } from './LookupServiceWindow'
import { INITIAL_SELECTED } from './transformSelectedJob'
import { useCopyService } from './useCopyService'
interface ModifiedServiceExpanded extends ServiceItem {
  repairOrderExpanded: RepairOrderExpanded
}

export const PreviousServices: FC<
  SearchServiceProps & {
    defer: boolean
    filteredRepairOrders: RepairOrderExpanded[] | undefined
    loadingRepairOrders: boolean
    services: ServiceExpanded[] | undefined
    loadingServices: boolean
  }
> = ({
  defer,
  submitCallback,
  cancelCallback,
  selected,
  setSelected,
  filteredRepairOrders,
  loadingRepairOrders,
  services,
  loadingServices
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const { customer, repairOrder, inventoryParts } =
    useContext(RepairOrderContext)

  const { selectedLaborMatrixId } = useSelectPricingMatrix(
    customer?.information?.defaultPriceLevel || '',
    '',
    ''
  )
  const vehicleId = repairOrder!.entry!.info!.vehicle!
  const repairOrderId = repairOrder!.entry!.id!
  const [updateService] = useUpdateServiceMutation()
  const { setIsSubmitting } = useContext(SubmitDialogContext)

  const repairOrderIdMap = useMemo(() => {
    const map: { [key: string]: RepairOrderExpanded } = {}
    ;(filteredRepairOrders || []).forEach((ro) => (map[ro.entry!.id!] = ro))
    return map
  }, [filteredRepairOrders])

  const filteredServices = useMemo(
    () =>
      services
        ? services
            .filter((s) => s.entry!.info!.recommendInFutureRepairOrders)
            .filter((s) =>
              defer
                ? s.entry!.info!.status === ServiceInfoStatusEnum.Deferred ||
                  s.entry!.info!.status === ServiceInfoStatusEnum.Recommended
                : s.entry!.info!.status === ServiceInfoStatusEnum.Authorized
            )
        : undefined,
    [defer, services]
  )

  const transformedServices: ModifiedServiceExpanded[] | undefined =
    useTransformServiceItems(filteredServices, false, vehicleId, inventoryParts)
      ?.map((s) => ({
        ...s,
        repairOrderExpanded: repairOrderIdMap[s.entry!.info!.repairOrder!]
      }))
      .sort(
        (a, b) =>
          b.repairOrderExpanded.entry!.created!.valueOf() -
          a.repairOrderExpanded.entry!.created!.valueOf()
      )

  const handleHide = useCallback(
    async (serviceEntry: ServiceEntry) => {
      setIsSubmitting(true)
      await updateService({
        authorization: accessToken,
        serviceEntry: {
          ...serviceEntry,
          info: {
            ...serviceEntry!.info!,
            recommendInFutureRepairOrders: false
          }
        }
      })
      setIsSubmitting(false)
    },
    [accessToken, setIsSubmitting, updateService]
  )

  const { handleSubmitCopyService: copyService } = useCopyService()

  const handleSubmitCopyService = useCallback(
    async (service: ServiceExpanded | undefined) => {
      if (!service || !selectedLaborMatrixId) {
        return
      }
      await copyService(
        repairOrderId,
        service,
        selectedLaborMatrixId,
        setIsSubmitting,
        submitCallback
      )
    },
    [
      copyService,
      repairOrderId,
      selectedLaborMatrixId,
      setIsSubmitting,
      submitCallback
    ]
  )

  useEffect(() => {
    setSelected(INITIAL_SELECTED)
  }, [defer, setSelected])

  return (
    <>
      <AddServiceDialogContentWrapper>
        <LoadingMultipleArrayDataQueriesWrapper
          dataAndLoading={[
            {
              data: filteredRepairOrders?.map((f) => f.entry!.id!),
              loading: loadingRepairOrders
            },
            {
              data: services?.map((s) => s.entry!.id!),
              loading: loadingServices
            }
          ]}>
          <Paper
            component={List}
            variant="outlined"
            sx={{
              overflowY: 'auto',
              height: '100%',
              boxSizing: 'border-box'
            }}
            disablePadding>
            <NoResultListItem
              noResult={
                !!(
                  (filteredRepairOrders && filteredRepairOrders.length === 0) ||
                  (filteredServices && filteredServices.length === 0)
                )
              }
            />
            {(transformedServices || []).map((serviceItem) => (
              <ExpandPanel
                key={serviceItem.id!}
                id={serviceItem.id!}
                singleSelect={false}
                handleClick={(newStatus) => {
                  if (newStatus) {
                    setSelected({
                      ...INITIAL_SELECTED,
                      service: services?.find(
                        (s) => s.entry!.id === serviceItem.id!
                      )
                    })
                  } else {
                    setSelected({
                      ...INITIAL_SELECTED
                    })
                  }
                }}
                checked={
                  !!(
                    selected.service &&
                    selected.service.entry!.id === serviceItem.id
                  )
                }
                mainElements={
                  <Stack
                    sx={{ width: '100%' }}
                    direction={'row'}
                    justifyContent="space-between">
                    <Stack>
                      <Typography variant="subtitle1">
                        {serviceItem.name}
                      </Typography>
                      <Typography variant="body2">
                        Created On:{' '}
                        {dayjs(
                          serviceItem.repairOrderExpanded!.entry!.created!
                        ).format('MMMM D, YYYY')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>
                        {formatCurrency(serviceItem.cachedRetailPrice!, true)}
                      </Typography>
                      {defer && (
                        <Button
                          onClick={handleHide.bind(null, serviceItem.entry!)}
                          variant="text">
                          Hide
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                }
                hiddenElements={
                  <>
                    {serviceItem.entry!.info!.description && (
                      <Typography>
                        {serviceItem.entry!.info!.description}
                      </Typography>
                    )}
                    <List disablePadding>
                      {serviceItem.laborOverrides!.length > 0 && (
                        <ListItem selected={true}>
                          <Typography variant="subtitle2">Labor</Typography>
                        </ListItem>
                      )}
                      {serviceItem.laborOverrides!.map((laborOverride) => {
                        const motorApplication: LaborApplication | undefined =
                          serviceItem.motorApplications![laborOverride.id!]
                        return (
                          <ListItem key={laborOverride.id!}>
                            <ListItemText
                              primary={getLaborName(
                                laborOverride,
                                motorApplication
                              )}
                            />
                          </ListItem>
                        )
                      })}
                      {serviceItem.partsClaimed.length +
                        serviceItem.partsInCart.length +
                        serviceItem.partsStandAlone.length !==
                        0 && (
                        <ListItem selected={true}>
                          <Typography variant="subtitle2">Parts</Typography>
                        </ListItem>
                      )}
                      {[
                        ...serviceItem.partsClaimed,
                        ...serviceItem.partsInCart,
                        ...serviceItem.partsStandAlone
                      ].map((part) => (
                        <ListItem key={part.id!}>
                          <ListItemText
                            primary={`(${part.quantity}) x ${part.partName}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                }
              />
            ))}
          </Paper>
        </LoadingMultipleArrayDataQueriesWrapper>
      </AddServiceDialogContentWrapper>
      <DialogActions>
        <Button onClick={() => cancelCallback()}>Cancel</Button>
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmitCopyService.bind(null, selected.service)}
          disabled={!selected.service}>
          Save
        </Button>
      </DialogActions>
    </>
  )
}
